"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import {
  DownloadIcon,
  GlobeIcon,
  HelpCircleIcon,
  MonitorIcon,
  MoonIcon,
  MoreHorizontalIcon,
  SunIcon,
} from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { useContext, useTransition } from "react";
import { switchLanguage } from "@/app/[lang]/action";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useRouter } from "@/lib/navigation";
import { ExportStatusContext } from "../../export/export-status-context";
import { getQuickStartGuideUrl } from "./quick-start-guide-links";

const languages = [
  { code: "ja", label: "日本語" },
  { code: "en", label: "English" },
];

export function HeaderUtilityOverflowMenu({
  includeHelp = false,
  includeExportStatus = true,
}: {
  includeHelp?: boolean;
  includeExportStatus?: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { toast } = useToast();
  const [isPending, startTransition] = useTransition();
  const { setTheme, theme } = useTheme();
  const { t } = useLingui();
  const { onOpen: openExportStatus } = useContext(ExportStatusContext);

  const currentLang = pathname.split("/")[1] || "en";
  const guideUrl = getQuickStartGuideUrl(currentLang);
  const label = t`More options`;

  const handleSwitchLanguage = (locale: string) => {
    if (locale === currentLang) return;

    startTransition(async () => {
      const { error } = await switchLanguage(
        locale,
        pathname,
        searchParams.toString(),
      );

      if (error) {
        toast({
          title: t`Error`,
          description: error,
          variant: "destructive",
        });
        router.refresh();
      }
    });
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={label}
              disabled={isPending}
            >
              <MoreHorizontalIcon className="h-[1rem] w-[1rem]" />
              <span className="sr-only">
                <Trans>More options</Trans>
              </span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            <Trans>More options</Trans>
          </p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        {includeExportStatus ? (
          <DropdownMenuItem onSelect={openExportStatus}>
            <DownloadIcon className="size-4 shrink-0" />
            <Trans>Export Status</Trans>
          </DropdownMenuItem>
        ) : null}
        {includeHelp ? (
          <DropdownMenuItem asChild>
            <a href={guideUrl} target="_blank" rel="noopener noreferrer">
              <HelpCircleIcon className="size-4 shrink-0" />
              <Trans>Help</Trans>
            </a>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <GlobeIcon className="size-4 shrink-0" />
            <Trans>Switch language</Trans>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={currentLang}
              onValueChange={handleSwitchLanguage}
            >
              {languages.map((language) => (
                <DropdownMenuRadioItem
                  key={language.code}
                  value={language.code}
                  disabled={isPending || currentLang === language.code}
                >
                  {language.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <MoonIcon className="size-4 shrink-0" />
            <Trans>Toggle theme</Trans>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
              <DropdownMenuRadioItem value="light">
                <SunIcon className="size-4 shrink-0" />
                <Trans>Light</Trans>
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">
                <MoonIcon className="size-4 shrink-0" />
                <Trans>Dark</Trans>
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">
                <MonitorIcon className="size-4 shrink-0" />
                <Trans>System</Trans>
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
